import React, { ReactNode } from "react";
import ReactMarkdown, { Components, Options } from "react-markdown";
import Link from "../../ui/Link/Link";
import Typography, { Heading } from "../../ui/Typography/Typography";

const replaceBrStrings = (children: ReactNode): ReactNode =>
	React.Children.map(children, (child) => {
		if (typeof child === "string") {
			return child
				.split(/<br\s*\/?>/gi)
				.reduce<ReactNode[]>((result, segment, index) => {
					if (index === 0) {
						return [segment];
					}
					return [
						...result,
						<br key={`line-break-${index}`} aria-hidden="true" />,
						segment,
					];
				}, []);
		}

		if (React.isValidElement(child)) {
			const element = child as React.ReactElement<{ children?: ReactNode }>;
			return React.cloneElement(element, {
				children: replaceBrStrings(element.props.children),
			});
		}

		return child;
	});

const components = {
	h1: ({ node, children, ...rest }: { node: unknown; children: ReactNode }) => (
		<Heading as="h1" className="title" {...rest}>
			{replaceBrStrings(children)}
		</Heading>
	),
	h2: ({ node, children, ...rest }: { node: unknown; children: ReactNode }) => (
		<Heading as="h2" className="subtitle" {...rest}>
			{replaceBrStrings(children)}
		</Heading>
	),
	h3: ({ node, children, ...rest }: { node: unknown; children: ReactNode }) => (
		<Heading as="h3" className="subtitle--secondary" {...rest}>
			{replaceBrStrings(children)}
		</Heading>
	),
	p: ({ node, children, ...rest }: { node: unknown; children: ReactNode }) => (
		<Typography className="body-text" {...rest}>
			{replaceBrStrings(children)}
		</Typography>
	),
	a: ({ node, children, ...rest }: { node: unknown; children: ReactNode }) => {
		const isExternal = (rest as { href: string }).href.startsWith("http");
		const link = {
			url: (rest as { href: string }).href,
		};
		return (
			<Link link={link} isExternal={isExternal}>
				{children}
			</Link>
		);
	},
};

const MarkdownRenderer = (props: Readonly<Options>) => (
	<ReactMarkdown components={components as Components} {...props} />
);

export default MarkdownRenderer;
